import React, { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useSnackbar } from 'notistack'
import { Icon } from '@iconify/react'
import { CircularProgress, Popover } from '@material-ui/core'
import { track } from '@amplitude/analytics-browser'
import { colors } from 'src/components/app-ui'
import CustomDomainNonPremiumView from 'src/components/custom-domain-non-premium-view'
import { PatchContent, PatchData } from 'src/model/patch'
import { collectionShowcaseAction } from 'src/reducers/collection/action'
import { courseCollectionListAction } from 'src/reducers/course-collection/action'
import {
  addCustomDomainAction,
  domainDeleteAction,
  domainListAction,
  domainPatchAction,
  domainRetryAction,
} from 'src/reducers/domain/action'
import safeTrack from 'src/utils/safe-track'
import useStyles from './styles'

const CNAME_VALUE = 'custom.minicoursegenerator.com'
const CNAME_HELP_URL =
  'https://intercom.help/mini-course-generator/en/articles/7864258-customizing-links-with-custom-domain-cname'
const PAGE_SIZES = [5, 10, 25]

// CustomDomainVerificationStatus: 0 Pending, 1 Verified, 2 NotVerified, 3 PreVerified
const STATUS = {
  1: {
    label: 'Verified',
    icon: 'tabler:circle-check-filled',
    color: colors.successDark,
    weight: 500,
  },
  2: {
    label: 'CNAME record not matched.',
    icon: 'tabler:alert-triangle',
    color: colors.warningDark,
  },
  pending: {
    label: 'Checking DNS…',
    icon: 'tabler:refresh',
    color: colors.muted,
  },
}

const KIND_OPTIONS = [
  { value: 'portal', label: 'Portal' },
  { value: 'path', label: 'A learning path' },
  { value: 'course', label: 'A course' },
]

const KIND_ICONS = {
  portal: 'tabler:world',
  path: 'tabler:route',
  course: 'tabler:book-2',
}

/** What a domain opens, from its default collection / course. */
const domainTarget = (domain, showcaseId) => {
  if (domain.defaultCourseDto) {
    return {
      kind: 'course',
      id: domain.defaultCourseDto.id,
      label: domain.defaultCourseDto.name,
    }
  }
  if (domain.defaultCollectionDto) {
    const portal = domain.defaultCollectionDto.id === showcaseId
    return {
      kind: portal ? 'portal' : 'path',
      id: domain.defaultCollectionDto.id,
      label: portal ? 'Portal' : domain.defaultCollectionDto.collectionName,
    }
  }
  return { kind: null, id: null, label: '—' }
}

/** The domain fields the API takes for a kind + target. */
const targetPayload = (kind, targetId, showcaseId) =>
  kind === 'course'
    ? {
        type: 'Course',
        defaultCourseId: Number(targetId),
        defaultCollectionId: null,
      }
    : {
        type: 'Collection',
        defaultCourseId: null,
        defaultCollectionId: kind === 'portal' ? showcaseId : targetId,
      }

const Select = ({ value, onChange, children }) => {
  const classes = useStyles()
  return (
    <div className={classes.selectWrap}>
      <select
        className={classes.select}
        value={value ?? ''}
        onChange={event => onChange(event.target.value)}
      >
        {children}
      </select>
      <span className={classes.chevron}>
        <Icon icon="tabler:chevron-down" width={16} height={16} />
      </span>
    </div>
  )
}

/** "Opens by default" + the path / course it opens. */
const TargetFields = ({
  kind,
  target,
  onKind,
  onTarget,
  paths,
  courses,
  portalTaken,
  showHint,
}) => {
  const classes = useStyles()
  const options = kind === 'course' ? courses : paths
  return (
    <>
      <div className={classes.field}>
        Opens by default
        <Select value={kind} onChange={onKind}>
          {KIND_OPTIONS.map(option => (
            <option
              key={option.value}
              value={option.value}
              disabled={option.value === 'portal' && Boolean(portalTaken)}
            >
              {option.label}
            </option>
          ))}
        </Select>
        {showHint && (
          <span className={classes.hint}>
            What visitors see when they open the domain itself.
          </span>
        )}
        {portalTaken && (
          <span className={classes.hintMuted}>
            Portal already has a domain
            {typeof portalTaken === 'string' ? ` (${portalTaken})` : ''}.
          </span>
        )}
      </div>
      {kind !== 'portal' && (
        <div className={classes.field}>
          {kind === 'course' ? 'Course' : 'Learning path'}
          <Select value={target} onChange={onTarget}>
            <option value="" disabled>
              Select {kind === 'course' ? 'a course' : 'a learning path'}
            </option>
            {options.map(option => (
              <option key={option.id} value={option.id}>
                {option.name}
              </option>
            ))}
          </Select>
        </div>
      )}
    </>
  )
}

const Dialog = ({ onClose, children }) => {
  const classes = useStyles()
  return createPortal(
    <div className={classes.backdrop} onClick={onClose}>
      <div
        className={classes.dialog}
        role="dialog"
        aria-modal="true"
        onClick={event => event.stopPropagation()}
      >
        {children}
      </div>
    </div>,
    document.body,
  )
}

/**
 * Workspace › Custom domain: add a CNAME, add the subdomain and pick what it
 * opens (portal, a learning path or a course), then manage the domains.
 * Uses the existing custom domain endpoints.
 */
const CustomDomainTab = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const { enqueueSnackbar } = useSnackbar()
  const list = useSelector(state => state.domain.list)

  const [step, setStep] = useState(1)
  const [copied, setCopied] = useState(false)
  const [subdomain, setSubdomain] = useState('')
  const [kind, setKind] = useState(null)
  const [target, setTarget] = useState('')
  const [adding, setAdding] = useState(false)

  const [showcaseId, setShowcaseId] = useState(null)
  const [paths, setPaths] = useState([])
  const [courses, setCourses] = useState([])

  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(5)
  const [loading, setLoading] = useState(true)

  const [menu, setMenu] = useState(null) // { anchor, domain }
  const [editing, setEditing] = useState(null) // { domain, kind, target }
  const [deleting, setDeleting] = useState(null)
  const copyTimer = useRef()

  const isBasic = window.CONSTANTS?.USER_ROLE_ID === 1

  const loadDomains = useCallback(
    (nextPage = page, nextSize = pageSize) => {
      setLoading(true)
      dispatch(
        domainListAction(
          {
            CurrentPage: nextPage,
            PageSize: nextSize,
            SortingColumn: 'createdAt',
            Asc: false,
            FilterText: '',
            Filters: [],
          },
          () => setLoading(false),
        ),
      )
    },
    [dispatch, page, pageSize],
  )

  useEffect(() => {
    if (isBasic) return
    loadDomains(0, pageSize)
    // The portal is the workspace's showcase collection.
    dispatch(
      collectionShowcaseAction({}, response => setShowcaseId(response?.id)),
    )
    dispatch(
      courseCollectionListAction(
        {
          CurrentPage: 0,
          PageSize: 200,
          SortingColumn: 'LastModifiedAt',
          Asc: false,
          FilterText: '',
          Filters: [],
        },
        data => {
          setPaths(
            (data.courseCollectionDtos || []).map(item => ({
              id: item.id,
              name: item.collectionName,
            })),
          )
          setCourses(
            (data.courseDtos || []).map(item => ({
              id: item.id,
              name: item.name,
            })),
          )
        },
      ),
    )
    return () => clearTimeout(copyTimer.current)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, isBasic])

  const pathOptions = paths.filter(path => path.id !== showcaseId)
  const domains = list?.results || []
  const total = list?.totalCount || 0
  const portalDomain = domains.find(
    item => item.defaultCollectionDto?.id === showcaseId,
  )
  const currentKind = kind || (portalDomain ? 'path' : 'portal')
  const validSubdomain = /^[a-z0-9-]+(\.[a-z0-9-]+)+$/i.test(subdomain.trim())
  const canAdd =
    validSubdomain &&
    !adding &&
    (currentKind === 'portal' ? Boolean(showcaseId) : Boolean(target))

  const copyCname = () => {
    navigator.clipboard?.writeText(CNAME_VALUE)
    setCopied(true)
    clearTimeout(copyTimer.current)
    copyTimer.current = setTimeout(() => setCopied(false), 1800)
  }

  const addDomain = () => {
    if (!canAdd) return
    setAdding(true)
    dispatch(
      addCustomDomainAction(
        {
          domain: subdomain.trim().toLowerCase(),
          ...targetPayload(currentKind, target, showcaseId),
        },
        () => {
          setAdding(false)
          setSubdomain('')
          setKind(null)
          setTarget('')
          setStep(1)
          setPage(0)
          loadDomains(0, pageSize)
          safeTrack('added_custom_domain', { target: currentKind })
          enqueueSnackbar('Domain added', { variant: 'success' })
        },
      ),
    )
    // The saga skips the callback on failure (it shows its own error).
    setTimeout(() => setAdding(false), 15000)
  }

  const saveEdit = () => {
    if (!editing) return
    const { domain, kind: nextKind, target: nextTarget } = editing
    if (nextKind !== 'portal' && !nextTarget) return
    const payload = targetPayload(nextKind, nextTarget, showcaseId)
    const [clearPath, setPath, value] =
      payload.type === 'Course'
        ? ['defaultCollectionId', 'defaultCourseId', payload.defaultCourseId]
        : [
            'defaultCourseId',
            'defaultCollectionId',
            payload.defaultCollectionId,
          ]
    // Same two patches as the old edit modal: clear the other target, set this one.
    dispatch(
      domainPatchAction(
        new PatchData(
          new PatchContent(null, clearPath, PatchData.OPERATION_REPLACE),
          domain.id,
        ),
        () => {},
      ),
    )
    dispatch(
      domainPatchAction(
        new PatchData(
          new PatchContent(value, setPath, PatchData.OPERATION_REPLACE),
          domain.id,
        ),
        () => {
          setEditing(null)
          loadDomains()
          enqueueSnackbar('Domain updated', { variant: 'success' })
        },
      ),
    )
  }

  const verify = domain => {
    setMenu(null)
    dispatch(
      domainRetryAction(domain.id, () => {
        loadDomains()
        enqueueSnackbar('Checking DNS again…', { variant: 'success' })
      }),
    )
  }

  const confirmDelete = () => {
    const domain = deleting
    setDeleting(null)
    dispatch(
      domainDeleteAction(domain.id, () => {
        safeTrack('deleted_custom_domain', {
          target: domainTarget(domain, showcaseId).kind,
        })
        const lastOnPage = domains.length === 1 && page > 0
        const nextPage = lastOnPage ? page - 1 : page
        setPage(nextPage)
        loadDomains(nextPage, pageSize)
      }),
    )
  }

  if (isBasic) {
    return (
      <CustomDomainNonPremiumView
        onClickUpgrade={() => {
          track('limit_warning_upgrade', { from: 'cname_limit_upgrade' })
          window.location.href = '/pricing'
        }}
      />
    )
  }

  const stepMark = (n, done) => ({
    background: done
      ? colors.successDark
      : step === n
      ? colors.primary
      : '#CBD0D7',
  })
  const stepColor = n => (step === n ? colors.navy : colors.subtle)
  const lastPage = Math.max(0, Math.ceil(total / pageSize) - 1)
  const goTo = nextPage => {
    if (nextPage < 0 || nextPage > lastPage || nextPage === page) return
    setPage(nextPage)
    loadDomains(nextPage, pageSize)
  }
  const pageButton = (icon, title, nextPage, disabled) => (
    <button
      type="button"
      title={title}
      className={classes.pageButton}
      style={{
        color: disabled ? colors.disabled : colors.navy,
        cursor: disabled ? 'default' : 'pointer',
      }}
      disabled={disabled}
      onClick={() => goTo(nextPage)}
    >
      <Icon icon={icon} width={18} height={18} />
    </button>
  )

  return (
    <div className={classes.root}>
      <div className={classes.steps}>
        <div className={classes.step}>
          <div className={classes.rail}>
            <span className={classes.mark} style={stepMark(1, step > 1)}>
              {step > 1 ? (
                <Icon icon="tabler:check" width={14} height={14} />
              ) : (
                1
              )}
            </span>
            <span className={classes.line} />
          </div>
          <div className={classes.stepBody} style={{ paddingBottom: 16 }}>
            <button
              type="button"
              className={classes.stepTitle}
              style={{ color: stepColor(1), cursor: 'pointer' }}
              onClick={() => setStep(1)}
            >
              Add this CNAME to your DNS records
            </button>
            {step === 1 && (
              <>
                <div className={classes.stepText}>
                  In your domain provider, create a CNAME record for the
                  subdomain you want to use and point it to the value below.{' '}
                  <a
                    href={CNAME_HELP_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    How to add a CNAME record
                  </a>
                </div>
                <div className={classes.cname}>
                  <span className={classes.cnameLabel}>CNAME</span>
                  <code className={classes.cnameValue}>{CNAME_VALUE}</code>
                  <button
                    type="button"
                    className={classes.copy}
                    onClick={copyCname}
                  >
                    <Icon
                      icon={copied ? 'tabler:check' : 'tabler:copy'}
                      width={12}
                      height={12}
                    />
                    {copied ? 'Copied' : 'Copy'}
                  </button>
                </div>
                <button
                  type="button"
                  className={classes.primary}
                  onClick={() => setStep(2)}
                >
                  I've added it, next
                </button>
              </>
            )}
          </div>
        </div>

        <div className={classes.step}>
          <span className={classes.mark} style={stepMark(2, false)}>
            2
          </span>
          <div className={classes.stepBody} style={{ gap: 14 }}>
            <span className={classes.stepTitle} style={{ color: stepColor(2) }}>
              Add your subdomain
            </span>
            {step === 2 && (
              <>
                <label className={classes.field}>
                  Subdomain
                  <input
                    className={classes.input}
                    value={subdomain}
                    placeholder="academy.yourcompany.com"
                    onChange={event => setSubdomain(event.target.value)}
                  />
                </label>
                <TargetFields
                  kind={currentKind}
                  target={target}
                  onKind={value => {
                    setKind(value)
                    setTarget('')
                  }}
                  onTarget={setTarget}
                  paths={pathOptions}
                  courses={courses}
                  portalTaken={portalDomain ? portalDomain.domain : null}
                  showHint
                />
                <button
                  type="button"
                  className={classes.primary}
                  disabled={!canAdd}
                  onClick={addDomain}
                >
                  {adding ? 'Adding…' : 'Add subdomain'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      <div className={classes.table}>
        <div className={classes.tableTitle}>Your domains</div>
        <div className={classes.columns}>
          <span>Domain</span>
          <span>Opens by default</span>
          <span>Status</span>
          <span />
        </div>
        {loading && domains.length === 0 ? (
          <div className={classes.empty}>
            <CircularProgress size={22} />
          </div>
        ) : domains.length === 0 ? (
          <div className={classes.empty}>
            No custom domains yet. Add one with the steps above.
          </div>
        ) : (
          domains.map(domain => {
            const opens = domainTarget(domain, showcaseId)
            const status = STATUS[domain.verificationStatus] || STATUS.pending
            return (
              <div key={domain.id} className={classes.row}>
                <span className={classes.domain} title={domain.domain}>
                  {domain.domain}
                </span>
                <span className={classes.target}>
                  <span style={{ display: 'inline-flex', color: colors.muted }}>
                    {opens.kind && (
                      <Icon
                        icon={KIND_ICONS[opens.kind]}
                        width={14}
                        height={14}
                      />
                    )}
                  </span>
                  <span>{opens.label}</span>
                </span>
                <span
                  className={classes.status}
                  style={{
                    color: status.color,
                    fontWeight: status.weight || 400,
                  }}
                >
                  <Icon icon={status.icon} width={16} height={16} />
                  {status.label}
                </span>
                <button
                  type="button"
                  title="More actions"
                  className={classes.more}
                  onClick={event =>
                    setMenu({ anchor: event.currentTarget, domain })
                  }
                >
                  <Icon icon="tabler:dots-vertical" width={16} height={16} />
                </button>
              </div>
            )
          })
        )}
        <div className={classes.pager}>
          <span className={classes.perPage}>
            Rows per page:
            <span
              className={classes.selectWrap}
              style={{ display: 'inline-flex' }}
            >
              <select
                className={classes.perSelect}
                value={pageSize}
                onChange={event => {
                  const size = Number(event.target.value)
                  setPageSize(size)
                  setPage(0)
                  loadDomains(0, size)
                }}
              >
                {PAGE_SIZES.map(size => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
              <span className={classes.chevron} style={{ right: 2 }}>
                <Icon icon="tabler:chevron-down" width={14} height={14} />
              </span>
            </span>
          </span>
          <span>
            {total === 0
              ? '0–0 of 0'
              : `${page * pageSize + 1}–${Math.min(
                  (page + 1) * pageSize,
                  total,
                )} of ${total}`}
          </span>
          <span style={{ display: 'inline-flex', gap: 2 }}>
            {pageButton('tabler:chevrons-left', 'First page', 0, page === 0)}
            {pageButton(
              'tabler:chevron-left',
              'Previous page',
              page - 1,
              page === 0,
            )}
            {pageButton(
              'tabler:chevron-right',
              'Next page',
              page + 1,
              page >= lastPage,
            )}
            {pageButton(
              'tabler:chevrons-right',
              'Last page',
              lastPage,
              page >= lastPage,
            )}
          </span>
        </div>
      </div>

      <Popover
        open={Boolean(menu)}
        anchorEl={menu?.anchor}
        onClose={() => setMenu(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ className: classes.menu, elevation: 0 }}
      >
        <button
          type="button"
          className={classes.menuItem}
          onClick={() => {
            const opens = domainTarget(menu.domain, showcaseId)
            setEditing({
              domain: menu.domain,
              kind: opens.kind || 'path',
              target: opens.kind === 'portal' ? '' : opens.id ?? '',
            })
            setMenu(null)
          }}
        >
          <Icon icon="tabler:pencil" width={15} height={15} />
          Edit Domain
        </button>
        <button
          type="button"
          className={classes.menuItem}
          onClick={() => verify(menu.domain)}
        >
          <Icon icon="tabler:refresh" width={15} height={15} />
          Verify Again
        </button>
        <div className={classes.menuDivider} />
        <button
          type="button"
          className={classes.menuDanger}
          onClick={() => {
            setDeleting(menu.domain)
            setMenu(null)
          }}
        >
          <Icon icon="tabler:trash" width={15} height={15} />
          Delete
        </button>
      </Popover>

      {editing && (
        <Dialog onClose={() => setEditing(null)}>
          <div className={classes.dialogHead}>
            <div>
              <div className={classes.dialogTitle}>Edit Domain</div>
              <div className={classes.dialogSub}>{editing.domain.domain}</div>
            </div>
            <button
              type="button"
              className={classes.dialogClose}
              onClick={() => setEditing(null)}
            >
              <Icon icon="tabler:x" width={18} height={18} />
            </button>
          </div>
          <TargetFields
            kind={editing.kind}
            target={editing.target}
            onKind={value =>
              setEditing({ ...editing, kind: value, target: '' })
            }
            onTarget={value => setEditing({ ...editing, target: value })}
            paths={pathOptions}
            courses={courses}
            portalTaken={portalDomain && portalDomain.id !== editing.domain.id}
          />
          <div className={classes.dialogActions}>
            <button
              type="button"
              className={classes.textButton}
              onClick={() => setEditing(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className={classes.primary}
              disabled={editing.kind !== 'portal' && !editing.target}
              onClick={saveEdit}
            >
              Save
            </button>
          </div>
        </Dialog>
      )}

      {deleting && (
        <Dialog onClose={() => setDeleting(null)}>
          <div className={classes.dialogTitle}>Remove {deleting.domain}?</div>
          <div className={classes.dialogSub}>
            Visitors will no longer reach your content from this address.
          </div>
          <div className={classes.dialogActions}>
            <button
              type="button"
              className={classes.textButton}
              onClick={() => setDeleting(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className={classes.dangerButton}
              onClick={confirmDelete}
            >
              Remove
            </button>
          </div>
        </Dialog>
      )}
    </div>
  )
}

export default CustomDomainTab
